import { useState } from 'react'

import { changeAosrDate, useCheckOperation } from '@builddocs/data-management'
import { AOSRType, SecondaryActType } from '@builddocs/filestore-api'
import { Notification } from '@builddocs/mui'
import { useToast } from '@builddocs/system'
import {
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import { DatePicker } from '@mui/x-date-pickers'
import { format } from 'date-fns'
import formatISO from 'date-fns/formatISO'
import { Controller, useForm } from 'react-hook-form'

import { Button } from '../../atoms'
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '../../molecules'

interface IFieldsForm {
  newAosrDate: string
}

export const ChangeAosrDate = ({
  act,
  disabled,
}: {
  act?: AOSRType | SecondaryActType
  disabled?: boolean
  refreshAct: () => void
}) => {
  const { toast } = useToast()

  const { errors, handleSubmit, control, watch } = useForm<IFieldsForm>()
  const newAosrDate = watch('newAosrDate')

  const [confirmed, setConfirmed] = useState(false)
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [isUpdating, setIsUpdating] = useState(false)

  const { checkOperation } = useCheckOperation()
  const handleModalOpen = () => {
    checkOperation({
      operationId: 'UPDATE_ACT_AOSR_DATE',
      operationCallback: () => {
        setModalOpen(true)
      },
    })
  }

  const handleModalClose = () => {
    setModalOpen(false)
  }

  const handleConfirm = () => {
    setConfirmed(v => !v)
  }

  const handleSend = async () => {
    if (!act) return
    const transformedDate = formatISO(new Date(newAosrDate), {
      representation: 'date',
    })

    if (!transformedDate || transformedDate.length !== 10)
      return toast.error('Некорректная дата')
    setIsUpdating(true)
    await changeAosrDate({
      actDate: transformedDate,
      aosrId: act?.id,
      journalId: act?.journalId,
      versionRemarkExon: act?.versionRemarkExon,
    })
      .then(() => {
        toast.success('Данные успешно обновлены')
        // refreshAct()
        handleModalClose()
      })
      .catch(e => {
        toast.error(
          e.response.data?.message ??
            'Произошла ошибка во время обновления даты',
        )
      })
      .finally(() => setIsUpdating(false))
  }

  return (
    <>
      <MenuItem disabled={disabled} onClick={handleModalOpen}>
        Изменить дату АОСР
      </MenuItem>
      <form>
        {/* баблинг приводит к активации кнопок меню по hotkey */}
        <Modal
          open={modalOpen}
          onClose={handleModalClose}
          onKeyDown={e => e.stopPropagation()}
        >
          <ModalContent
            sx={{
              maxWidth: '640px',
            }}
          >
            <ModalHeader onClose={handleModalClose}>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 500,
                  color: 'rgba(0,0,0,0.87)',
                }}
              >
                Изменение даты АОСР
              </Typography>
            </ModalHeader>
            <ModalBody
              sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <Notification
                message="Дата подписания акта должна соответствовать дате освидетельствования работ, но не позднее даты начала последующих работ и не ранее окончания выполненных работ в соответствии с общим журналом работ"
                sx={{
                  marginBottom: '16px',
                }}
              />
              <DatePicker
                readOnly
                label="Текущая дата акта"
                mask="__.__.____"
                renderInput={params => (
                  <TextField data-cy="old-aosr-date" {...params} />
                )}
                value={act?.actDate ?? null}
                onChange={() => {}}
              />
              <Controller
                key="newAosrDate"
                control={control}
                errors={errors}
                name="newAosrDate"
                render={({ onChange, value, ...rest }) => (
                  <DatePicker
                    {...rest}
                    clearable
                    label="Новая дата акта"
                    mask="__.__.____"
                    maxDate={new Date()}
                    renderInput={params => (
                      <TextField
                        data-cy="new-aosr-date"
                        error={!!errors['newAosrDate']}
                        helperText={errors['newAosrDate']?.message}
                        {...params}
                      />
                    )}
                    shouldDisableDate={date => {
                      return format(date, 'yyyy-MM-dd') === act?.actDate
                    }}
                    value={value ?? null}
                    onChange={newValue => {
                      onChange(newValue)
                    }}
                  />
                )}
                rules={{
                  required: 'Должно быть заполнено',
                  validate: value =>
                    !isNaN(Date.parse(value)) || 'Некорректная дата',
                }}
              />
              <FormControlLabel
                control={<Checkbox checked={confirmed} />}
                label={
                  <Typography color="#333333">
                    Подтверждаю изменение даты акта
                  </Typography>
                }
                onChange={handleConfirm}
              />
            </ModalBody>
            <ModalFooter sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
              <Button
                disabled={newAosrDate === undefined || isUpdating || !confirmed}
                sx={{
                  bgcolor: '#0034AD',
                  color: '#ffffff',
                  px: 2,
                  py: 1,
                  '&:hover': { bgcolor: '#002E9C' },
                  '&:disabled': { bgcolor: '#7F99D6', color: '#ffffff' },
                }}
                type="submit"
                onClick={handleSubmit(handleSend)}
              >
                {isUpdating ? 'Обновление' : 'Сохранить'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </form>
    </>
  )
}

export default ChangeAosrDate
